import { useState, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { Play, RotateCcw } from "lucide-react";
import { useLang, fill } from "../../i18n/LanguageContext";

const PADS = [
  { id: 0, color: "#F59E0B", freq: 329.6 },
  { id: 1, color: "#EF4444", freq: 392.0 },
  { id: 2, color: "#22C55E", freq: 440.0 },
  { id: 3, color: "#3B82F6", freq: 523.3 },
];

export const RhythmTap = () => {
  const { t } = useLang();
  const s = t.games.rhythm;
  const [sequence, setSequence] = useState([]);
  const [active, setActive] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [status, setStatus] = useState(s.intro);
  const [best, setBest] = useState(0);
  const inputIdx = useRef(0);
  const acRef = useRef(null);

  const tone = useCallback((freq) => {
    try {
      if (!acRef.current) acRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const ac = acRef.current;
      const osc = ac.createOscillator();
      const gain = ac.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, ac.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.25, ac.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime + 0.35);
      osc.connect(gain);
      gain.connect(ac.destination);
      osc.start();
      osc.stop(ac.currentTime + 0.36);
    } catch (e) { /* audio optional */ }
  }, []);

  const flash = useCallback((id) => {
    setActive(id);
    tone(PADS[id].freq);
    setTimeout(() => setActive(null), 320);
  }, [tone]);

  const playSequence = useCallback((seq) => {
    setPlaying(true);
    setStatus(s.watch);
    seq.forEach((id, i) => setTimeout(() => flash(id), (i + 1) * 620));
    setTimeout(() => {
      setPlaying(false);
      setStatus(s.turn);
      inputIdx.current = 0;
    }, (seq.length + 1) * 620);
  }, [flash, s.watch, s.turn]);

  const next = useCallback(() => {
    const seq = [...sequence, Math.floor(Math.random() * 4)];
    setSequence(seq);
    setTimeout(() => playSequence(seq), 500);
  }, [sequence, playSequence]);

  const start = () => {
    setSequence([]);
    setStatus(s.ready);
    const seq = [Math.floor(Math.random() * 4)];
    setSequence(seq);
    setTimeout(() => playSequence(seq), 600);
  };

  const tap = (id) => {
    if (playing || sequence.length === 0) return;
    flash(id);
    if (id === sequence[inputIdx.current]) {
      inputIdx.current += 1;
      if (inputIdx.current === sequence.length) {
        setBest((b) => Math.max(b, sequence.length));
        setStatus(fill(s.cleared, { n: sequence.length }));
        setTimeout(next, 900);
      }
    } else {
      setStatus(fill(s.oops, { n: sequence.length }));
      setBest((b) => Math.max(b, sequence.length - 1));
      setSequence([]);
    }
  };

  return (
    <div data-testid="game-rhythm" className="flex flex-col items-center">
      <div className="mb-4 flex w-full items-center justify-between text-sm text-white/70">
        <span>{s.round}: <span className="font-display text-lg text-gold-light">{sequence.length}</span></span>
        <span>{s.best}: <span className="font-display text-lg text-gold-light">{best}</span></span>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {PADS.map((p) => (
          <motion.button
            key={p.id}
            onClick={() => tap(p.id)}
            disabled={playing}
            data-testid={`rhythm-pad-${p.id}`}
            className="h-28 w-28 rounded-2xl border border-white/10 sm:h-32 sm:w-32"
            animate={{
              backgroundColor: active === p.id ? p.color : `${p.color}33`,
              boxShadow: active === p.id ? `0 0 30px ${p.color}` : "0 0 0px transparent",
              scale: active === p.id ? 1.05 : 1,
            }}
            transition={{ duration: 0.15 }}
          />
        ))}
      </div>

      <p className="mt-5 h-6 text-center text-sm text-white/70" data-testid="rhythm-status">{status}</p>

      <button onClick={start} disabled={playing} data-testid="rhythm-start" className="mt-3 flex items-center gap-2 rounded-full bg-gradient-to-r from-gold-light to-gold-primary px-6 py-3 text-sm font-medium text-ink-900 disabled:opacity-50">
        {sequence.length ? <RotateCcw className="h-4 w-4" /> : <Play className="h-4 w-4" />}
        {sequence.length ? s.restart : s.start}
      </button>
    </div>
  );
};
